import React, { useEffect, useState, useCallback } from 'react'
import UrlsVsIncidentTable from './UrlsVsIncident'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
    fetchIncidentAnalysisData,
    fetchIncidentAnalysisDataExcel,
} from '../../../../../../../utils/api/reportAPI'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import toasters from '../../../../../../../utils/toasters/Toasters'

const formatPayloadDates = (payload) => {
    if (!payload) return payload

    const formattedPayload = { ...payload }

    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate)
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0]
        }
    }

    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate)
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0]
        }
    }

    return formattedPayload
}

const UrlsVsIncident = () => {
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const [payload, setPayload] = useState(null)
    const [exportType, setExportType] = useState('N')

    useEffect(() => {
        const startDate = searchParams.get('startDate')
        const endDate = searchParams.get('endDate')
        const code = searchParams.get('code')

        if (startDate || endDate) {
            const urlPayload = { startDate, endDate, code }
            setPayload(urlPayload)
            sessionStorage.setItem(
                'UrlsVsIncidentPayload',
                JSON.stringify(urlPayload)
            )
            return
        }

        if (location.state) {
            setPayload(location.state)
            sessionStorage.setItem(
                'UrlsVsIncidentPayload',
                JSON.stringify(location.state)
            )
            return
        }

        const storedPayload = sessionStorage.getItem('UrlsVsIncidentPayload')
        if (storedPayload) {
            setPayload(JSON.parse(storedPayload))
        }
    }, [location.state, searchParams])

    const formattedPayload = formatPayloadDates(payload)

    const getData = useCallback(async () => {
        return fetchIncidentAnalysisData({
            ...formattedPayload,
            export: exportType,
        })
    }, [formattedPayload, exportType])

    const { isLoading, data, isError, error } = useQuery(
        ['fetch-Url-Vs-Incidents', formattedPayload, exportType],
        getData,
        { enabled: !!formattedPayload }
    )

    // PDF Download
    const downloadPDF = () => {
        const doc = new jsPDF()

        doc.setFontSize(18)
        doc.text('URLs vs Incidents Report', 14, 22)

        doc.setFontSize(11)
        doc.setTextColor(100)
        doc.text(
            `Report Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
            14,
            32
        )

        const tableColumn = ['Type', 'Event Count', 'Incident Count', 'URL to Incident Percentage']

        // Single row (same as table UI)
        const tableRows = [
            [
                data?.name || 'URLs VS Incidents',
                data?.eventcount || 0,
                data?.incidentcount || 0,
                data?.percentage || 0,
            ],
        ]

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 40,
        })

        doc.save('urls-vs-incidents-report.pdf')
    }

    // Excel Download
    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' })
        const csvUrl = URL.createObjectURL(csvBlob)
        const hiddenElement = document.createElement('a')
        hiddenElement.href = csvUrl
        hiddenElement.download = `${fileName}.csv`
        hiddenElement.click()
    }

    const ExcelDownload = async () => {
        try {
            const response = await fetchIncidentAnalysisDataExcel({
                ...formattedPayload,
                export: 'Y',
            })
            downloadFile(response, 'URLs vs Incidents')
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message })
        }
    }

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <UrlsVsIncidentTable
                    data={data}
                    isLoading={isLoading}
                    DisplayDate={formattedPayload}
                    onPdfClick={downloadPDF}
                    onExcelClick={ExcelDownload}
                    reportData={formattedPayload}
                    reportTableData={data}
                    reportCode={payload?.code}
                />
            )}
        </>
    )
}

export default UrlsVsIncident
